import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';

/**
 * StaffMetrics PrimeNG preset.
 *
 * Built on Aura, overriding semantic + component tokens so PrimeNG components
 * render from the same design tokens as the Tailwind utilities.
 *
 * ── How this works ──────────────────────────────────────────────────────────
 * Values are `var(--sm-*)` references, not literals. Those variables live in
 * `src/styles.scss` (`:root` and `.dark`) and are the single source of truth
 * shared with `tailwind.config.js`. Because the variables already switch on
 * `.dark`, the light and dark colour schemes below deliberately point at the
 * SAME references — the variable swaps itself, so there is nothing to keep in
 * sync here.
 *
 * ── Where to make changes ───────────────────────────────────────────────────
 * Colour, radius or spacing that should apply app-wide  → src/styles.scss
 * Appearance of a specific PrimeNG component            → this file
 * Layout, flow, grid, positioning                       → Tailwind in templates
 *
 * Never restyle a PrimeNG component with `::ng-deep` in a component stylesheet.
 * See .claude/skills/staffmetrics-ui/ (SKILL.md, "Rules for PrimeNG + Tailwind").
 */
export const StaffMetricsPreset = definePreset(Aura, {
  semantic: {
    /**
     * Radius scale. The design system has one rule per value:
     *   lg (8px)  → every interactive control  (buttons, inputs, menu items, tabs)
     *   xl (12px) → every container            (cards, panels, dialogs, overlays)
     * `md` is kept at 6px only because Aura references it internally; nothing in
     * this preset should point at it.
     */
    borderRadius: {
      none: '0',
      xs: '2px',
      sm: '4px',
      md: '6px',
      lg: '8px',
      xl: '12px',
    },

    /**
     * Primary action colour — the same `--sm-primary-*` variables that
     * `tailwind.config.js` reads, so PrimeNG components and raw Tailwind
     * `primary-*` classes resolve to one value and cannot drift apart.
     *
     * Blue, not red. The design system states it directly in the reference
     * token file — `--accent: #1F4DDB /* primary action *\/` alongside
     * `--brand-red: #EF4443 /* brand mark only *\/` — and white on the red
     * measures 3.76:1, which cannot carry a button label. Red remains the
     * brand mark (`--sm-brand-red`) and the destructive colour
     * (`--sm-danger`).
     */
    primary: {
      50: 'var(--sm-primary-50)',
      100: 'var(--sm-primary-100)',
      200: 'var(--sm-primary-200)',
      300: 'var(--sm-primary-300)',
      400: 'var(--sm-primary-400)',
      500: 'var(--sm-primary-500)',
      600: 'var(--sm-primary-600)',
      700: 'var(--sm-primary-700)',
      800: 'var(--sm-primary-800)',
      900: 'var(--sm-primary-900)',
      950: 'var(--sm-primary-950)',
    },

    /**
     * Form controls. This is the highest-leverage block in the file.
     *
     * Aura's select, datepicker, autocomplete, multiselect, textarea and
     * inputnumber all reference `{form.field.*}`, so defining it here styles
     * every control at once. The previous preset defined `components.inputtext`
     * instead, which is why only plain text inputs picked up the theme and
     * `shared-data-view.component.scss` needed ~60 lines of ::ng-deep to force
     * the filter bar into line. That override is now redundant.
     *
     * paddingY 0.5rem yields a ~38px control, matching the height the filter
     * bar was already pinned to.
     */
    formField: {
      paddingX: '0.75rem',
      paddingY: '0.5rem',
      borderRadius: '{border.radius.lg}',
      focusRing: {
        width: '3px',
        style: 'solid',
        color: 'var(--sm-focus-ring)',
        offset: '0',
        shadow: 'none',
      },
      sm: { fontSize: '0.8125rem', paddingX: '0.625rem', paddingY: '0.325rem' },
      lg: { fontSize: '1rem', paddingX: '0.875rem', paddingY: '0.65rem' },
    },

    /** Focus ring for everything that is not a form control. */
    focusRing: {
      width: '2px',
      style: 'solid',
      color: 'var(--sm-accent)',
      offset: '2px',
    },

    content: { borderRadius: '{border.radius.xl}' },

    /**
     * Overlay geometry AND shadow live here in the base block, not in
     * `colorScheme.light`. Aura defines these at the base level, and the base
     * value wins in the emitted `:root` rule — a light-scheme override is
     * silently dropped (the dark one lands, which makes the bug easy to miss).
     * Since `--sm-shadow-*` already swaps under `.dark`, base is also correct.
     */
    overlay: {
      select: { borderRadius: '{border.radius.xl}', shadow: 'var(--sm-shadow-lifted)' },
      popover: { borderRadius: '{border.radius.xl}', shadow: 'var(--sm-shadow-lifted)' },
      modal: { borderRadius: '{border.radius.xl}', shadow: 'var(--sm-shadow-deep)' },
    },

    colorScheme: {
      light: {
        surface: {
          0: 'var(--sm-card)',
          50: 'var(--sm-paper)',
          100: 'var(--sm-paper-2)',
          200: 'var(--sm-rule)',
          300: 'var(--sm-rule-strong)',
          400: 'var(--sm-field-border)',
          500: 'var(--sm-ink-3)',
          600: 'var(--sm-ink-3)',
          700: 'var(--sm-ink-2)',
          800: 'var(--sm-ink)',
          900: 'var(--sm-ink)',
          950: 'var(--sm-ink)',
        },
        primary: {
          color: 'var(--sm-accent)',
          contrastColor: 'var(--sm-ink-inverse)',
          hoverColor: 'var(--sm-accent-hover)',
          activeColor: 'var(--sm-accent-hover)',
        },
        highlight: {
          background: 'var(--sm-accent-soft)',
          focusBackground: 'var(--sm-accent-soft)',
          color: 'var(--sm-accent)',
          focusColor: 'var(--sm-accent)',
        },
        formField: {
          background: 'var(--sm-card)',
          borderColor: 'var(--sm-field-border)',
          hoverBorderColor: 'var(--sm-ink-3)',
          focusBorderColor: 'var(--sm-accent)',
          invalidBorderColor: 'var(--sm-danger)',
          color: 'var(--sm-ink)',
          placeholderColor: 'var(--sm-ink-3)',
          invalidPlaceholderColor: 'var(--sm-danger)',
          disabledBackground: 'var(--sm-paper-2)',
          disabledColor: 'var(--sm-ink-3)',
          filledBackground: 'var(--sm-paper-2)',
          iconColor: 'var(--sm-ink-3)',
          floatLabelColor: 'var(--sm-ink-3)',
          floatLabelFocusColor: 'var(--sm-accent)',
          floatLabelInvalidColor: 'var(--sm-danger)',
          // The design system separates surfaces with a hairline, not a shadow.
          shadow: 'none',
        },
        text: {
          color: 'var(--sm-ink)',
          hoverColor: 'var(--sm-ink)',
          mutedColor: 'var(--sm-ink-3)',
          hoverMutedColor: 'var(--sm-ink-2)',
        },
        content: {
          background: 'var(--sm-card)',
          hoverBackground: 'var(--sm-paper-2)',
          borderColor: 'var(--sm-rule)',
          color: 'var(--sm-ink)',
          hoverColor: 'var(--sm-ink)',
        },
        overlay: {
          select: {
            background: 'var(--sm-card)',
            borderColor: 'var(--sm-rule)',
            color: 'var(--sm-ink)',
          },
          popover: {
            background: 'var(--sm-card)',
            borderColor: 'var(--sm-rule)',
            color: 'var(--sm-ink)',
          },
          modal: {
            background: 'var(--sm-card)',
            borderColor: 'var(--sm-rule)',
            color: 'var(--sm-ink)',
          },
        },
        list: {
          option: {
            focusBackground: 'var(--sm-paper-2)',
            selectedBackground: 'var(--sm-accent-soft)',
            selectedFocusBackground: 'var(--sm-accent-soft)',
            color: 'var(--sm-ink-2)',
            focusColor: 'var(--sm-ink)',
            selectedColor: 'var(--sm-accent)',
            selectedFocusColor: 'var(--sm-accent)',
          },
        },
        mask: { background: 'var(--sm-backdrop)', color: 'var(--sm-ink)' },
        border: { color: 'var(--sm-rule)' },
      },

      /**
       * The `--sm-*` variables already redefine themselves under `.dark`, so
       * the dark scheme points at the same references. Do not duplicate values
       * here — change `src/styles.scss` instead.
       */
      dark: {
        surface: {
          0: 'var(--sm-card)',
          50: 'var(--sm-paper)',
          100: 'var(--sm-paper-2)',
          200: 'var(--sm-rule)',
          300: 'var(--sm-rule-strong)',
          400: 'var(--sm-field-border)',
          500: 'var(--sm-ink-3)',
          600: 'var(--sm-ink-3)',
          700: 'var(--sm-ink-2)',
          800: 'var(--sm-ink)',
          900: 'var(--sm-ink)',
          950: 'var(--sm-ink)',
        },
        primary: {
          color: 'var(--sm-accent)',
          contrastColor: 'var(--sm-ink-inverse)',
          hoverColor: 'var(--sm-accent-hover)',
          activeColor: 'var(--sm-accent-hover)',
        },
        highlight: {
          background: 'var(--sm-accent-soft)',
          focusBackground: 'var(--sm-accent-soft)',
          color: 'var(--sm-accent)',
          focusColor: 'var(--sm-accent)',
        },
        formField: {
          background: 'var(--sm-card)',
          borderColor: 'var(--sm-field-border)',
          hoverBorderColor: 'var(--sm-ink-3)',
          focusBorderColor: 'var(--sm-accent)',
          invalidBorderColor: 'var(--sm-danger)',
          color: 'var(--sm-ink)',
          placeholderColor: 'var(--sm-ink-3)',
          invalidPlaceholderColor: 'var(--sm-danger)',
          disabledBackground: 'var(--sm-paper-2)',
          disabledColor: 'var(--sm-ink-3)',
          filledBackground: 'var(--sm-paper-2)',
          iconColor: 'var(--sm-ink-3)',
          floatLabelColor: 'var(--sm-ink-3)',
          floatLabelFocusColor: 'var(--sm-accent)',
          floatLabelInvalidColor: 'var(--sm-danger)',
          shadow: 'none',
        },
        text: {
          color: 'var(--sm-ink)',
          hoverColor: 'var(--sm-ink)',
          mutedColor: 'var(--sm-ink-3)',
          hoverMutedColor: 'var(--sm-ink-2)',
        },
        content: {
          background: 'var(--sm-card)',
          hoverBackground: 'var(--sm-paper-2)',
          borderColor: 'var(--sm-rule)',
          color: 'var(--sm-ink)',
          hoverColor: 'var(--sm-ink)',
        },
        overlay: {
          select: {
            background: 'var(--sm-card)',
            borderColor: 'var(--sm-rule)',
            color: 'var(--sm-ink)',
          },
          popover: {
            background: 'var(--sm-card)',
            borderColor: 'var(--sm-rule)',
            color: 'var(--sm-ink)',
          },
          modal: {
            background: 'var(--sm-card)',
            borderColor: 'var(--sm-rule)',
            color: 'var(--sm-ink)',
          },
        },
        list: {
          option: {
            focusBackground: 'var(--sm-paper-2)',
            selectedBackground: 'var(--sm-accent-soft)',
            selectedFocusBackground: 'var(--sm-accent-soft)',
            color: 'var(--sm-ink-2)',
            focusColor: 'var(--sm-ink)',
            selectedColor: 'var(--sm-accent)',
            selectedFocusColor: 'var(--sm-accent)',
          },
        },
        mask: { background: 'var(--sm-backdrop)', color: 'var(--sm-ink)' },
        border: { color: 'var(--sm-rule)' },
      },
    },
  },

  components: {
    /* ── Containers: 12px radius, hairline border, no shadow ─────────────── */
    card: {
      root: { borderRadius: '{border.radius.xl}', shadow: 'none' },
      colorScheme: {
        light: { root: { background: 'var(--sm-card)', color: 'var(--sm-ink)' } },
        dark: { root: { background: 'var(--sm-card)', color: 'var(--sm-ink)' } },
      },
    },
    panel: {
      root: { borderRadius: '{border.radius.xl}', borderColor: '{border.color}' },
      header: { background: 'transparent', borderColor: '{border.color}' },
    },
    dialog: {
      root: { borderRadius: '{border.radius.xl}', shadow: 'var(--sm-shadow-deep)' },
    },

    /* ── Controls: 8px radius ────────────────────────────────────────────── */
    button: {
      root: {
        borderRadius: '{border.radius.lg}',
        paddingX: '1rem',
        paddingY: '0.5rem',
        gap: '0.5rem',
        label: { fontWeight: '500' },
        sm: { paddingX: '0.75rem', paddingY: '0.375rem', fontSize: '0.8125rem' },
        lg: { paddingX: '1.5rem', paddingY: '0.75rem', fontSize: '1rem' },
      },
    },
    menu: {
      root: { borderRadius: '{border.radius.xl}', borderColor: '{border.color}' },
      item: { borderRadius: '{border.radius.lg}' },
      list: { padding: '0.25rem', gap: '0.125rem' },
    },
    tabs: {
      tablist: { borderColor: '{border.color}' },
      tab: { borderColor: 'transparent', fontWeight: '500' },
      tabpanel: { background: 'transparent' },
    },
    paginator: {
      root: { background: 'transparent', padding: '0.75rem 1rem', gap: '0.125rem' },
      navButton: {
        borderRadius: '{border.radius.lg}',
        width: '2rem',
        height: '2rem',
      },
    },
    /* Status chips. The badge grammar lives in app-status-badge; this only
       keeps stray `p-tag` uses from looking foreign. */
    tag: { root: { borderRadius: '{border.radius.sm}', fontWeight: '500' } },

    /* ── Data table ──────────────────────────────────────────────────────── */
    datatable: {
      root: { borderColor: '{border.color}' },
      header: {
        background: 'var(--sm-paper-2)',
        borderColor: '{border.color}',
        color: 'var(--sm-ink-3)',
      },
      headerCell: {
        background: 'var(--sm-paper-2)',
        hoverBackground: 'var(--sm-paper-2)',
        color: 'var(--sm-ink-3)',
        borderColor: '{border.color}',
      },
      columnTitle: { fontWeight: '500' },
      row: {
        background: 'var(--sm-card)',
        hoverBackground: 'var(--sm-paper-2)',
        selectedBackground: 'var(--sm-accent-soft)',
        color: 'var(--sm-ink-2)',
        hoverColor: 'var(--sm-ink)',
        selectedColor: 'var(--sm-accent)',
      },
      bodyCell: { borderColor: '{border.color}' },
      footer: { background: 'var(--sm-paper-2)', borderColor: '{border.color}' },
      paginatorBottom: { borderColor: '{border.color}' },
    },

    /* ── Toast: semantic colour on a card surface, not a solid fill ──────── */
    toast: {
      root: { borderRadius: '{border.radius.xl}', borderWidth: '1px' },
      colorScheme: {
        light: {
          success: {
            background: 'var(--sm-success-soft)',
            borderColor: 'var(--sm-success)',
            color: 'var(--sm-success-text)',
            detailColor: 'var(--sm-ink-2)',
          },
          error: {
            background: 'var(--sm-danger-soft)',
            borderColor: 'var(--sm-danger)',
            color: 'var(--sm-danger)',
            detailColor: 'var(--sm-ink-2)',
          },
          warn: {
            background: 'var(--sm-warning-soft)',
            borderColor: 'var(--sm-warning)',
            color: 'var(--sm-warning-text)',
            detailColor: 'var(--sm-ink-2)',
          },
          info: {
            background: 'var(--sm-accent-soft)',
            borderColor: 'var(--sm-accent)',
            color: 'var(--sm-accent)',
            detailColor: 'var(--sm-ink-2)',
          },
        },
      },
    },

    tooltip: {
      root: {
        borderRadius: '{border.radius.lg}',
        background: 'var(--sm-ink)',
        color: 'var(--sm-paper)',
      },
    },
    skeleton: { root: { borderRadius: '{border.radius.lg}' } },
  },
});
